import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { styled } from "@mui/material";
import { useActionChoices } from "stores";
import { DurationInput, Select } from "components";
import NoContentFound from "components/NoContentFound/NoContentFound";
import { useSelectedFieldContext } from "context/SelectedFieldProvider";
import { TASKS } from "constants/actions";
// import { EQUIPMENT_TYPES } from "constants/equipmentTypes";
import EquipmentItem from "./EquipmentItem";

const ContainerStyled = styled("div")({
  "& .duration-input": {
    marginTop: "8px",
    width: "150px",
  },
});

const NoContentFoundStyled = styled(NoContentFound)({
  height: "60px",
  "& .text": {
    width: "80%",
  },
});

const EquipmentItemMemo = memo(EquipmentItem);

const EquipmentUsed = ({ isInEditMode, className }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  const { equipments } = useSelectedFieldContext();
  const { getActionById } = useActionChoices();

  const { values, setFieldValue } = useFormikContext();
  const shouldShowDurationInput = Boolean(values?.equipment);

  let actionBaseName;
  switch (getActionById(values?.taskId)) {
    case TASKS.watering:
      actionBaseName = "waterAction";
      break;
    case TASKS.diagnose:
      actionBaseName = "diagnoseAction";
      break;
    case TASKS.fertilize:
      actionBaseName = "fertilizeAction";
      break;
    case TASKS.other:
      actionBaseName = "otherAction";
      break;
    default:
      break;
  }

  const equipmentsForThisAction = useMemo(() => {
    const list = [values?.taskId, values?.[actionBaseName]?.operationTypeId];

    return equipments
      .filter(({ machineryRead }) =>
        machineryRead?.operations?.some((operation) =>
          list.includes(operation),
        ),
      )
      .map((equipment) => {
        const { id, machineryRead } = equipment;
        return {
          ...equipment,
          value: id,
          label: isEn ? machineryRead.nameEn : machineryRead.nameGr,
        };
      });
  }, [equipments, values, actionBaseName, isEn]);

  // const equipmentCategories = [
  //   {
  //     value: EQUIPMENT_TYPES.MACHINE,
  //     title: t("Equipment.Machines"),
  //   },
  //   {
  //     value: EQUIPMENT_TYPES.TOOL,
  //     title: t("Equipment.Tools"),
  //   },
  // ];

  // const equipmentOptions = equipmentsForThisAction.map((equipment) => ({
  //   value: equipment.id,
  //   label: equipment.machineryRead[isEn ? "nameEn" : "nameGr"],
  //   group: equipment.machineryRead.machineryType,
  // }));

  const handleEquipmentChange = useCallback(
    (e) => {
      setFieldValue("equipment", [
        {
          duration: 0,
          id: e.target.value,
        },
      ]);
    },
    [setFieldValue],
  );

  const handleDurationChange = useCallback(
    (duration) => {
      const currentEquipment = values?.equipment?.[0];

      if (currentEquipment) {
        setFieldValue("equipment", [
          {
            ...currentEquipment,
            duration: duration,
          },
        ]);
      }
    },
    [setFieldValue, values?.equipment],
  );

  if (!(isInEditMode || Boolean(values.equipment?.length))) {
    return (
      <NoContentFoundStyled text={t("ActionModal.Equipment.NoneSelected")} />
    );
  }

  return equipmentsForThisAction?.length > 0 ? (
    <ContainerStyled className={className}>
      <Select
        value={values?.equipment?.[0]?.id}
        onChange={handleEquipmentChange}
        isDisabled={!isInEditMode}
        isViewMode={!isInEditMode}
        // groups={equipmentCategories}
        options={equipmentsForThisAction}
        itemRenderer={({ option }) => <EquipmentItemMemo equipment={option} />}
        // renderValue={(value) => }
      />
      {shouldShowDurationInput && (
        <DurationInput
          placeholder={t("DurationInput.Label") + "*"}
          value={values?.equipment?.[0]?.duration}
          onChange={handleDurationChange}
          isViewMode={!isInEditMode}
          isDisabled={!isInEditMode}
          units={t("DurationInput.Minutes")}
          className="duration-input"
        />
      )}
    </ContainerStyled>
  ) : (
    <NoContentFoundStyled text={t("ActionModal.Equipment.NotFound")} />
  );
};

export default EquipmentUsed;
